import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button } from '@kcm/ui'
import { ErrorCode, type AmlValue } from '@kcm/contracts'
import { useTranslate } from '@tolgee/react'
import { DynamicForm, missingRequired } from '../components/DynamicForm'
import { pathForStep } from '../components/FunnelLayout'
import { useSite } from '../components/SiteProvider'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'
import { useGoToCurrentStep } from '../hooks/useResume'

/**
 * Step 3 — compliance (PLAN.md §13.3, §27.3).
 *
 * The questionnaire is not written here. Compliance maintains it in the CRM,
 * and which declaration applies is decided by the user's country — Thailand and
 * Taiwan have their own. This screen renders whatever the API normalised out of
 * that config, so a new question is a CRM edit rather than a deploy.
 *
 * The submit is synchronous against the CRM (§16.1), and the user is waiting on
 * it. That is why a failure has to be recoverable in place — the answers stay
 * in state and the button comes back, rather than the form emptying and asking
 * for all sixteen again.
 */
export function StepCompliance() {
  const navigate = useNavigate()
  const site = useSite()
  const { t } = useTranslate()
  const goToCurrentStep = useGoToCurrentStep()
  const [values, setValues] = useState<Record<string, AmlValue>>({})

  const form = useQuery({
    queryKey: ['aml-form'],
    queryFn: ({ signal }) => endpoints.amlForm(signal),
    // The API already caches the CRM config for an hour and serves it stale if
    // the CRM is down; re-asking on every window focus only adds latency.
    staleTime: 5 * 60 * 1000,
  })

  const submit = useMutation({
    mutationFn: () => endpoints.aml({ values }),
    onSuccess: (res) => navigate(pathForStep(res.step), { replace: true }),
  })

  const loadError = form.error instanceof ApiError ? form.error : null
  const submitError = submit.error instanceof ApiError ? submit.error : null

  // Both gates belong to the server, and both mean this screen cannot proceed
  // at all — so they replace it rather than sitting on top of a dead form.
  const blocked = loadError ?? submitError
  if (blocked?.code === ErrorCode.KYC_NOT_READY) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="info" title={t('compliance.stillCheckingTitle')}>
          {blocked.message}
        </Alert>
        <Button onClick={() => navigate('/identity', { replace: true })}>
          {t('compliance.backToVerification')}
        </Button>
      </div>
    )
  }
  if (blocked?.code === ErrorCode.STEP_NOT_AVAILABLE) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="info" title={t('gate.stepToFinishTitle')}>
          {blocked.message}
        </Alert>
        <Button onClick={goToCurrentStep}>{t('gate.continueWhereLeft')}</Button>
      </div>
    )
  }

  if (form.isPending) {
    return (
      <p role="status" className="text-sm text-muted">
        {t('compliance.loading')}
      </p>
    )
  }

  if (!form.data) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="error" title={t('compliance.loadFailedTitle')}>
          {loadError?.message ?? 'Please try again in a moment.'}
        </Alert>
        <Button onClick={() => void form.refetch()}>{t('common.tryAgain')}</Button>
        <p className="text-sm text-muted">
          {t('compliance.stillStuck')}{' '}
          <a className="underline" href={`mailto:${site.supportEmail}`}>
            {site.supportEmail}
          </a>
        </p>
      </div>
    )
  }

  const outstanding = missingRequired(form.data.fields, values)

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!submit.isPending) submit.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">
          {form.data.title || t('compliance.title')}
        </h1>
        <p className="mt-1 text-sm text-muted">{t('compliance.subtitle')}</p>
      </div>

      {submitError && (
        <Alert tone="error" title={titleFor(submitError, t)}>
          {submitError.message}
        </Alert>
      )}

      <DynamicForm
        fields={form.data.fields}
        values={values}
        errors={submitError?.fields}
        disabled={submit.isPending}
        onChange={(key, value) => setValues((v) => ({ ...v, [key]: value }))}
      />

      <Button type="submit" disabled={submit.isPending || outstanding.length > 0}>
        {submit.isPending ? t('compliance.submitting') : t('compliance.submit')}
      </Button>

      {/* Named as a count rather than a disabled button with no explanation:
          on a sixteen-question form, "why can't I continue" is the whole
          problem (§11.5). */}
      {outstanding.length > 0 && (
        <p role="status" className="text-sm text-muted">
          {outstanding.length === 1
            ? t('compliance.oneQuestionLeft')
            : t('compliance.questionsLeft', { count: outstanding.length })}
        </p>
      )}
    </form>
  )
}

/** Short heading per error class; the body is always the server's own copy. */
function titleFor(error: ApiError, t: (key: string) => string): string {
  switch (error.code) {
    case ErrorCode.RATE_LIMITED:
      return t('err.tooManyAttempts')
    case ErrorCode.VALIDATION_FAILED:
      return t('err.checkAnswers')
    case ErrorCode.CRM_UNAVAILABLE:
      return t('err.temporarilyUnavailable')
    default:
      return t('compliance.saveAnswersFailed')
  }
}
